/**
 * CR-7743: the two supplier production cells of a PO line row — what is still
 * to make, and how long is left to make it.
 *
 * The warehouse and fulfilment cells beside them live with the other quantity
 * cells in POLineRowQtyCells. Every figure comes from the list payload;
 * nothing here is recomputed.
 */

import { PurchaseOrderLine } from './types';
import { getDaysLeftToProduce } from './daysLeftToProduce';
import { useFormatters } from '../../../../contexts/PreferencesContext';

interface CallOffSupplyCellProps {
  columnId: string;
  line: PurchaseOrderLine;
}

/** A quantity as a number, or null when the payload has no figure for it. */
function quantityOf(value: string | null | undefined): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * One supplier production cell.
 *
 * @param columnId - remaining_to_produce_qty or days_left_to_produce
 * @param line - the row's PO line
 * @returns the table cell
 */
export default function CallOffSupplyCell({ columnId, line }: CallOffSupplyCellProps) {
  const { formatQuantity } = useFormatters();

  if (columnId === 'remaining_to_produce_qty') {
    // Null is "no figure" (a frozen row) and renders as a dash, never a 0.
    const remaining = quantityOf(line.remaining_to_produce_qty);
    return (
      <td className="py-3 px-4 text-right">
        <span className={`text-sm font-medium ${remaining !== null && remaining > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
          {formatQuantity(remaining)}
        </span>
      </td>
    );
  }

  const daysLeft = getDaysLeftToProduce(line.supplier_promise_date, line.remaining_to_produce_qty);
  return (
    <td className="py-3 px-4 text-center">
      {daysLeft ? (
        <span
          className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${daysLeft.className}`}
          data-tone={daysLeft.tone}
        >
          {daysLeft.label}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">-</span>
      )}
    </td>
  );
}
